import {
    IActivateOrderPayload,
    IActivatePayload,
    IConfirmEmailPayload,
    IFaqQuestion,
    IUpdateCustomerContactPayload,
    IUpdateCustomerCostCenterPayload,
    IUpdateCustomerInvoicingPayload
} from '@/interface/services'
import MyAxios, {BaseAxios} from '@/api/axios'
import {IPublicCustomerDTO} from '@/interface'
import {IDefaultResponse} from "@/interface/services/response/default.interface"
import {ICustomer, ICustomerLinkEnergy, ICustomerWithContract} from "@/interface/services/response/customer.interface"
import {
    IFinalizeRecoverUserIdPayload,
    ILinkPayload,
    ILogoutPayload
} from "@/interface/services/request/user-payload.interface"
import {IRelogUser, IUser} from "@/interface/services/response/user.interface"
import {useAbortController} from '@/composables/useAbortController'
import {AxiosError, AxiosResponse} from 'axios'

export default
{
    async activateContract(leadId: string, contractId: number): Promise<ICustomerWithContract> {
        const url = `/customer/${leadId}/contract/${contractId}/activate-sim`;
        return await MyAxios.post(url, {}).then(
            // eslint-disable-next-line
            (response: AxiosResponse<ICustomerWithContract>) => response.data
        );
    },

    async activateContractPublic(id: number, uuid: string, payload: IActivatePayload): Promise<ICustomerWithContract> {
        return await BaseAxios.post<ICustomerWithContract[]|void>(`${process.env.VUE_APP_URL_SERVICE_CUSTOMER_PUBLIC_PATH}/subscription/${uuid}/contract/${id}/order-delivery/activate`, payload)
            // eslint-disable-next-line
            .then((response:AxiosResponse<ICustomerWithContract|any>) => { return response.data })
            .catch((error:AxiosError) => { throw error })
    },

    async activateOrderPublic(payload: IActivateOrderPayload): Promise<ICustomerWithContract> {
        return await BaseAxios.post<ICustomerWithContract[]|void>(`${process.env.VUE_APP_URL_SERVICE_CUSTOMER_PUBLIC_PATH}/orders/activate`, payload)
            // eslint-disable-next-line
            .then((response:AxiosResponse<ICustomerWithContract|any>) => { return response.data })
            .catch((error:AxiosError) => { throw error })
    },

    async confirmEmailPublic(uuid: string, payload: IConfirmEmailPayload): Promise<IDefaultResponse> {
        return await BaseAxios.post<ICustomerWithContract[]|void>(`${process.env.VUE_APP_URL_SERVICE_CUSTOMER_PUBLIC_PATH}/subscription/${uuid}/confirm-email`, payload)
            // eslint-disable-next-line
            .then((response:AxiosResponse<IDefaultResponse|any>) => { return response.data })
            .catch((error:AxiosError) => { throw error })
    },

    async changeAppLanguage(language: string): Promise<IDefaultResponse> {
        const url = `/user/language`;
        return await MyAxios.post(url, { lang: language }).then(
            // eslint-disable-next-line
            (response: AxiosResponse<IDefaultResponse>) => response.data
        );
    },

    async getPublicCustomer(id: number, uuid: string): Promise<IPublicCustomerDTO> {
        return await BaseAxios.get<ICustomer[]|void>(`${process.env.VUE_APP_URL_SERVICE_CUSTOMER_PUBLIC_PATH}/customer/${id}/${uuid}`)
            // eslint-disable-next-line
            .then((response:AxiosResponse<IPublicCustomerDTO|any>) => {
                return response.data;
            })
            .catch((error:AxiosError) => {console.error(error);})
    },

    async getCustomer(id: number, withContracts: boolean, withLinkedUser = false): Promise<ICustomer> {
        const { getSignal } = useAbortController();
        const withContractsParam = withContracts ? "1" : "0";
        let url = `/customer/${id}/full?withContracts=${withContractsParam}`;
        if(withLinkedUser) url += `&withLinkedUser=1`;
        return await MyAxios.get(url, { ignoreGlobalLoader: true, signal: getSignal() }).then(
            (response: AxiosResponse<ICustomer>) => response.data
        );
    },

    async getLinkStatus(id: number): Promise<ICustomerLinkEnergy> {
        const { getSignal } = useAbortController();
        const url = `/customer/${id}/link-status`;
        return await MyAxios.get(url, { signal: getSignal() }).then(
            (response: AxiosResponse<ICustomerLinkEnergy>) => response.data
        );
    },

    async getFaq(lang: string): Promise<IFaqQuestion[]> {
        const langChecked = !lang || lang === "en" ? 'fr' : lang;
        const url = `/faq?lang=${langChecked}`;
        return await MyAxios.get(url).then(
            (response: AxiosResponse<IFaqQuestion[]>) => response.data
        );
    },

    async linkEnergy(payload: ILinkPayload): Promise<IUser> {
        const url = `/user/link`;
        return await MyAxios.post(url, payload, { ignoreGlobalLoader: true, ignoreUnauthorizedBehavior: true }).then(
            (response: AxiosResponse<IUser>) => response.data
        );
    },

    async logout(payload: ILogoutPayload): Promise<IDefaultResponse> {
        const url = `/user/logout`;
        return await MyAxios.post(url, payload, { ignoreUnauthorizedBehavior: true, ignoreGlobalError: true }).then(
            (response: AxiosResponse<IDefaultResponse>) => response.data
        );
    },

    async relogEnergy(): Promise<IRelogUser> {
        const url = `/user/relog`;
        return await MyAxios.post(url, {}).then(
            (response: AxiosResponse<IRelogUser>) => response.data
        );
    },

    async saveRecoveryLoggedUser(payload: IFinalizeRecoverUserIdPayload): Promise<AxiosResponse> {
        const url = `/user/password`;
        return await MyAxios.post(url, payload).then(
            // eslint-disable-next-line
            (response: AxiosResponse<any>) => response.data
        );
    },

    async updateContact(uuid: string, payload: IUpdateCustomerContactPayload): Promise<ICustomer> {
        const url = `/customer/${uuid}/contact`;
        return await MyAxios.patch(url, payload, { ignoreGlobalLoader: true, ignoreGlobalError: true }).then(
            // eslint-disable-next-line
            (response: AxiosResponse<ICustomer>) => response.data
        );
    },

    async updateInvoicing(uuid: string, payload: IUpdateCustomerInvoicingPayload): Promise<ICustomer> {
        const url = `/customer/${uuid}/invoicing`;
        return await MyAxios.patch(url, payload, { ignoreGlobalLoader: true, ignoreGlobalError: true }).then(
            // eslint-disable-next-line
            (response: AxiosResponse<ICustomer>) => response.data
        );
    },

    async updateCostCenter(uuid: string, payload: IUpdateCustomerCostCenterPayload): Promise<ICustomer> {
        const url = `/customer/${uuid}/cost-center`;
        return await MyAxios.patch(url, payload, { ignoreGlobalLoader: true, ignoreGlobalError: true }).then(
            // eslint-disable-next-line
            (response: AxiosResponse<ICustomer>) => response.data
        );
    },

    async updateContractIdentification(uuid: string, contractId: number, payload: any): Promise<any> {
        const url = `/customer/${uuid}/contract/${contractId}/identify`;
        return await MyAxios.post(url, payload, { ignoreGlobalLoader: true, ignoreGlobalError: true }).then(
            // eslint-disable-next-line
            response => response.data
        );
    },

    async updateContractPortIn(uuid: string, payload: any): Promise<any> {
        const url = `/customer/${uuid}/contract/`;
        return (await MyAxios.patch(url, payload, { ignoreGlobalLoader: true, ignoreGlobalError: true })).data;
    }
}
